import {
  ACCENT_RAMPS, MODULE_RAMPS, NEUTRAL_RAMPS,
  type AccentRamp, type GeneratedFamily, type ModuleKey, type NeutralRamp,
} from "./palettes.generated";
import type { ColorMode, PaletteColor, SemanticTokens, ThemeDef, ThemeId } from "./types";

/**
 * The theme registry.
 *
 * Slate is the original hand-tuned system and stays the default — its values here are byte-for-byte
 * what shipped before the registry existed, so switching to it changes nothing. The other three are
 * assembled from the generated OKLCH ramps in `palettes.generated.ts`.
 */

/* ------------------------------------------------------------------ shared tokens */

/**
 * Semantic colour, shared by every theme.
 *
 * Note `info` is a fixed blue rather than an alias of `primary`. It used to be the same object as
 * the slate accent, which was invisible while blue was the only accent — but under the teal theme
 * an aliased `info` would silently turn teal and stop reading as "informational". Pinning it here
 * keeps every theme's info chips, alerts and links recognisably blue. Under slate the value is
 * unchanged, so this is a no-op for the default theme.
 */
export const SEMANTICS: Record<ColorMode, SemanticTokens> = {
  light: {
    success: { main: "#17875A", light: "#5FBF93", dark: "#0F6644", contrastText: "#FFFFFF" },
    warning: { main: "#9A6410", light: "#C79433", dark: "#7A4E0A", contrastText: "#FFFFFF" },
    info:    { main: "#2F5AE0", light: "#5B8DEF", dark: "#234BC0", contrastText: "#FFFFFF" },
    error:   { main: "#B42318", light: "#DA7167", dark: "#8E1B12", contrastText: "#FFFFFF" },
  },
  dark: {
    success: { main: "#3FB483", light: "#6ECBA3", dark: "#1F7A54", contrastText: "#06230F" },
    warning: { main: "#D9A441", light: "#E7C179", dark: "#A5791F", contrastText: "#241B05" },
    info:    { main: "#5B8DEF", light: "#88AEF5", dark: "#2F5AE0", contrastText: "#08161F" },
    error:   { main: "#E6685C", light: "#EF9089", dark: "#B4392E", contrastText: "#260B07" },
  },
};

/** Secondary is a neutral action colour, not a brand one — shared, like the semantics. */
export const SECONDARY: Record<ColorMode, PaletteColor> = {
  light: { main: "#475569", light: "#64748B", dark: "#334155", contrastText: "#FFFFFF" },
  dark:  { main: "#94A3B8", light: "#B4C0D0", dark: "#64748B", contrastText: "#0B1019" },
};

/**
 * Department colour coding — reserved for small accents only (a 6–8px dot, an avatar, a
 * left-border), never large fills. Independent of the active theme: these identify a clinical
 * area, so they must not move when someone changes their palette.
 */
export type DepartmentKey =
  | "opd" | "emergency" | "ipd" | "pharmacy" | "laboratory"
  | "radiology" | "billing" | "administration" | "icu";

export const DEPARTMENT_COLORS: Record<DepartmentKey, { main: string; label: string }> = {
  opd:            { main: "#2F5AE0", label: "OPD" },
  emergency:      { main: "#B42318", label: "Emergency" },
  ipd:            { main: "#7C5CD6", label: "IPD" },
  pharmacy:       { main: "#17875A", label: "Pharmacy" },
  laboratory:     { main: "#4F58C4", label: "Laboratory" },
  radiology:      { main: "#0E7C8B", label: "Radiology" },
  billing:        { main: "#B4700F", label: "Billing" },
  administration: { main: "#5A6572", label: "Administration" },
  icu:            { main: "#A81E30", label: "ICU" },
};

/* ------------------------------------------------------------------ helpers */

/** "#RRGGBB" + alpha -> "rgba(...)". Only used for shell tints, which need literal strings. */
function rgba(hex: string, alpha: number): string {
  const h = hex.replace("#", "");
  const n = parseInt(h, 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
}

/**
 * Assembles one generated family into a ThemeDef.
 *
 * The step→role mapping is the whole point of having ramps: 700 is the lightest step that still
 * clears 4.5:1 for white button text, and 400 the darkest that stays legible on a dark surface, so
 * they are `primary.main` in light and dark respectively. MUI derives contained-button hover from
 * `.dark`, which is why light mode points it at 800 (deeper) and dark mode at 500 (also deeper) —
 * matching MUI's own convention that hover darkens in both modes.
 */
function fromRamps(id: ThemeId, label: string, blurb: string, family: GeneratedFamily): ThemeDef {
  const a: AccentRamp = ACCENT_RAMPS[family];
  const n: NeutralRamp = NEUTRAL_RAMPS[family];
  return {
    id,
    label,
    blurb,
    accent: {
      light: { main: a["700"], light: a["600"], dark: a["800"], contrastText: "#FFFFFF" },
      dark:  { main: a["400"], light: a["300"], dark: a["500"], contrastText: a["950"] },
    },
    neutral: {
      light: {
        background: n.n0,
        paper: n.n50,
        surface2: n.n100,
        textPrimary: n.n700,
        textSecondary: n.n600,
        textFaint: n.n500,
        divider: n.n200,
        dividerStrong: n.n300,
      },
      dark: {
        background: n.n850,
        paper: n.n800,
        surface2: n.n750,
        textPrimary: n.d300,
        textSecondary: n.d200,
        textFaint: n.d100,
        // Translucent dividers on dark surfaces so nested panels don't accumulate hard lines.
        divider: "rgba(255,255,255,0.09)",
        dividerStrong: "rgba(255,255,255,0.16)",
      },
    },
    shell: {
      // Two different steps, not one: n880 matches slate's light-mode sidebar lightness and n900
      // its dark-mode one. A single value made the dark sidebar the same lightness as the canvas.
      light: {
        bg: n.n880,
        text: "rgba(255,255,255,0.80)",
        border: "rgba(255,255,255,0.09)",
        brand: n.n0,
        activeBg: rgba(a["400"], 0.2),
        activeText: "#FFFFFF",
        hover: "rgba(255,255,255,0.06)",
      },
      dark: {
        bg: n.n900,
        text: "rgba(233,238,244,0.78)",
        border: "rgba(255,255,255,0.08)",
        brand: n.n0,
        activeBg: rgba(a["400"], 0.18),
        activeText: "#FFFFFF",
        hover: "rgba(255,255,255,0.05)",
      },
    },
  };
}

/* ------------------------------------------------------------------ the registry */

/**
 * Enterprise Slate — the original system, unchanged.
 *
 * The thesis is restraint: a cool-slate neutral canvas carries ~90% of the UI, a single confident
 * blue is the only accent, and success/warning/error appear only as state (never decoration).
 */
const SLATE: ThemeDef = {
  id: "slate",
  label: "Enterprise Slate",
  blurb: "Cool slate canvas, one confident blue. The default.",
  accent: {
    light: { main: "#2F5AE0", light: "#5B8DEF", dark: "#234BC0", contrastText: "#FFFFFF" },
    dark:  { main: "#5B8DEF", light: "#88AEF5", dark: "#2F5AE0", contrastText: "#0A1220" },
  },
  neutral: {
    light: {
      background: "#F6F7F9", // cool near-white, slight slate bias — chosen, not default grey
      paper: "#FFFFFF",
      surface2: "#FBFCFE",
      textPrimary: "#0F1826",
      textSecondary: "#5A6572",
      textFaint: "#8B95A1",
      divider: "#E4E8EE",
      dividerStrong: "#D3D9E1",
    },
    dark: {
      background: "#0B1019",
      paper: "#141B27",
      surface2: "#182130",
      textPrimary: "#EAEEF4",
      textSecondary: "#9BA7B6",
      textFaint: "#6E7B8C",
      divider: "rgba(255,255,255,0.09)",
      dividerStrong: "rgba(255,255,255,0.16)",
    },
  },
  shell: {
    light: {
      bg: "#101B2D", text: "rgba(255,255,255,0.80)", border: "rgba(255,255,255,0.09)",
      brand: "#F6F7F9", activeBg: "rgba(91,141,239,0.20)", activeText: "#FFFFFF", hover: "rgba(255,255,255,0.06)",
    },
    dark: {
      bg: "#0C1421", text: "rgba(233,238,244,0.78)", border: "rgba(255,255,255,0.08)",
      brand: "#F6F7F9", activeBg: "rgba(91,141,239,0.18)", activeText: "#FFFFFF", hover: "rgba(255,255,255,0.05)",
    },
  },
};

/**
 * Departmental — slate's canvas, seven accents.
 *
 * It is deliberately **one theme with a moving accent**, not seven themes: neutrals, semantics,
 * type, spacing and every component override stay fixed, and only `primary` follows the route.
 * That is what keeps it a design system rather than seven of them.
 *
 * The accent is ambient orientation, never information. Every module already has a distinct icon
 * and a text label, and every status already carries a word inside its pill, so nothing is lost to
 * a user who cannot separate the hues — which matters here because blue, indigo and purple are
 * adjacent by nature and OP, Lab and IP sit only ~22° apart.
 */
function moduleAccent(key: ModuleKey): Record<ColorMode, PaletteColor> {
  const r: AccentRamp = MODULE_RAMPS[key];
  return {
    light: { main: r["700"], light: r["600"], dark: r["800"], contrastText: "#FFFFFF" },
    dark: { main: r["400"], light: r["300"], dark: r["500"], contrastText: r["950"] },
  };
}

const DEPARTMENTAL: ThemeDef = {
  id: "departmental",
  label: "Departmental",
  blurb: "Slate canvas; the accent follows the module you are working in.",
  // Falls back to OP blue, which is today's accent — so an unmapped screen looks unchanged.
  accent: moduleAccent("op"),
  neutral: SLATE.neutral,
  shell: SLATE.shell,
  moduleAccents: {
    billing: moduleAccent("billing"),
    pharmacy: moduleAccent("pharmacy"),
    radiology: moduleAccent("radiology"),
    administration: moduleAccent("administration"),
    op: moduleAccent("op"),
    lab: moduleAccent("lab"),
    ip: moduleAccent("ip"),
  },
};

export const THEMES: Record<ThemeId, ThemeDef> = {
  slate: SLATE,
  turquoise: fromRamps(
    "turquoise", "Turquoise",
    "The vivid one. Greenest of the three, at the edge of what sRGB can show.",
    "turquoise",
  ),
  cyan: fromRamps(
    "cyan", "Cyan",
    "The blue-leaning one, and the softest switch from slate.",
    "cyan",
  ),
  teal: fromRamps(
    "teal", "Teal",
    "The muted one. Same hue neighbourhood, 26% less chroma at every step.",
    "teal",
  ),
  departmental: DEPARTMENTAL,
};

/** Registry order for the picker — default first, then vivid → cool → muted. */
export const THEME_ORDER: readonly ThemeId[] = ["slate", "turquoise", "cyan", "teal", "departmental"] as const;

export const DEFAULT_THEME_ID: ThemeId = "slate";

/** Narrows an untrusted string (query param, localStorage, API) to a known theme id. */
export function isThemeId(value: unknown): value is ThemeId {
  return typeof value === "string" && value in THEMES;
}
